import React from 'react';
import api from '../api';
import { getIcon } from '../../../../shared/utils/files';
import nanoid from 'nanoid';
import icons from '../../../../shared/utils/files/antIcons';
import getMess from '../translations';
import { notification } from 'antd';

import {
  readLocalFolders,
  getNotification,
  addChild,
  removeNotification,
  updateNotification,
  addNotification,
  getChild,
  updateChild,
  removeChild,
} from '../../../../shared/utils/files';
import { fileOwnerTypes } from '../../../../shared/constants/enums';
import { getDirTreeFromFilePaths, getGroupedFilesByFolder } from '../../../../shared/utils/files';

import { showValidationResultInfo, showRenameSelectedFilesModal } from '../../../../shared/utils/files/upload-helper';

const label = 'uploadFolder';

async function handler(apiOptions, actions) {
  const { navigateToDir, setParentsForResource, updateNotifications, getResource, getNotifications, getFiles, rootContainer, controlConfiguration } =
    actions;

  const getMessage = getMess.bind(null, apiOptions.locale);

  const notificationId = label;

  let uploadedCount = 0;
  let filesToUploadCount = 0;

  // todo: this has to be done also later in react router
  const preventNav = (event) => {
    if (uploadedCount === filesToUploadCount) return;

    event.preventDefault();

    // Chrome requires returnValue to be set.
    event.returnValue = '';
  };

  const onStart = (files) => {
    const notifications = getNotifications();
    const notification = getNotification(notifications, notificationId);
    let newChildren = [];
    if (notification && notification.children) {
      newChildren = notification.children;
    }

    files.forEach((file) => {
      const notificationChildId = nanoid();

      file.notificationChildId = notificationChildId;

      const childElement = {
        elementType: 'NotificationProgressItem',
        elementProps: {
          title: file.name,
          progress: 0,
          icon: getIcon({ name: file.name }),
          isSuccess: file?.isSuccess,
          message: file?.message,
        },
      };

      newChildren = addChild(newChildren || [], notificationChildId, childElement);
    });

    const newNotification = {
      title: newChildren.length > 1 ? getMessage('uploadingItems', { quantity: newChildren.length }) : getMessage('uploadingItem'),
      children: newChildren,
      closable: true,
      onHide: (_) => updateNotifications(removeNotification(notifications, notificationId)),
    };

    const newNotifications = notification
      ? updateNotification(notifications, notificationId, newNotification)
      : addNotification(notifications, notificationId, newNotification);

    updateNotifications(newNotifications);

    window.addEventListener('beforeunload', preventNav);
  };

  const onProgress = (notificationChildId, progress) => {
    const notifications = getNotifications();
    const notification = getNotification(notifications, notificationId);
    const child = getChild(notification.children, notificationChildId);
    const newChild = {
      ...child,
      element: {
        ...child.element,
        elementProps: {
          ...child.element.elementProps,
          progress,
        },
      },
    };

    const newChildren = updateChild(notification.children, notificationChildId, newChild);
    const newNotifications = updateNotification(notifications, notificationId, {
      children: newChildren,
    });

    updateNotifications(newNotifications);
  };

  const removeAllNotifications = () => {
    updateNotifications([]);
  };

  const handleFilesUpload = (files, fileUploadInfos) => {
    const filteredFiles = [];

    const uploadPromises = [];

    fileUploadInfos.forEach((info) => {
      const file = files.find((f) => f.name === info.fileName);

      if (!info.isSuccess) {
        file.message = info.message;
        file.renamed = info.renamed;
        file.newFileName = info.newFileName;
        file.isSuccess = info.isSuccess;
        uploadedCount++;
      } else {
        uploadPromises.push({
          presignedUrl: info.presignedUrl,
          parentId: info.parentId,
          fileId: info.newFileId,
          idOfFileToReplace: info.idOfFileToReplace,
          presignedUrlToReplace: info.presignedUrlToReplace,

          message: info.message,
          renamed: info.renamed,
          fileName: info.fileName,
          newFileName: info.newFileName,

          fileLength: file.size,
          fileType: file.contentType,
          file: file,
          onProgress: onProgress,
        });
      }

      filteredFiles.push(file);
    });

    if (uploadPromises.length) {
      onStart(filteredFiles);

      uploadPromises.forEach(async (item) => {
        const prom = api.uploadFileToS3Id({
          uploadUrl: !item.renamed && item.idOfFileToReplace ? item.presignedUrlToReplace : item.presignedUrl,
          file: item.file,
          onProgress: item.onProgress,
        });

        prom.then((uploadResp) => {
          api
            .confirmUpload({
              apiOptions: apiOptions,
              parentId: item.parentId,
              fileId: !item.renamed && item.idOfFileToReplace ? item.idOfFileToReplace : item.fileId,
              fileName: item.renamed ? item.newFileName : item.fileName,
              fileLength: item.fileLength,
            })
            .then((confResp) => {
              const notifications = getNotifications();
              const notification = getNotification(notifications, notificationId);
              const notificationChildrenCount = notification.children.length;
              const notificationChildId = item.file.notificationChildId;

              let newNotifications;

              if (notificationChildrenCount > 1) {
                newNotifications = updateNotification(notifications, notificationId, {
                  children: removeChild(notification.children, notificationChildId),
                });
              } else {
                newNotifications = removeNotification(notifications, notificationId);
              }

              updateNotifications(newNotifications);

              uploadedCount++;

              if (uploadedCount === filesToUploadCount) {
                removeAllNotifications();

                navigateToDir(resource.id, null, true);
                setParentsForResource(resource.id);

                window.removeEventListener('beforeunload', preventNav);
              }
            })
            .catch((err) => {
              uploadedCount++;

              if (uploadedCount === filesToUploadCount) {
                removeAllNotifications();

                navigateToDir(resource.id, null, true);
                setParentsForResource(resource.id);

                window.removeEventListener('beforeunload', preventNav);
              }
            });
        });
      });
    }
  };

  const createFolderTree = (files, resource, dirTree) => {
    api.createFolderTree(resource.id, dirTree).then((createFolderTreeResut) => {
      const groupedFilesByFolder = getGroupedFilesByFolder(createFolderTreeResut.data, files);
      for (const folderId in groupedFilesByFolder) {
        const filesByFolder = groupedFilesByFolder[folderId].files;
        if (folderId && filesByFolder) {
          api
            .getPresignedUrlsForUpload(apiOptions, {
              parentId: folderId,
              fileNames: filesByFolder.map((f) => {
                return {
                  fileName: f.name,
                  contentType: f.type,
                  size: f.size,
                };
              }),
            })
            .then((urlsResp) => {
              const fileUploadInfos = urlsResp.data;
              fileUploadInfos.forEach((fileUploadInfo) => {
                fileUploadInfo.parentId = folderId;
              });

              if (fileUploadInfos.length) {
                if (fileUploadInfos.findIndex((f) => f.isSuccess === false || f.renamed === true) >= 0) {
                  showValidationResultInfo(fileUploadInfos, rootContainer, () => {
                    if (fileUploadInfos.findIndex((f) => f.isSuccess === true) >= 0) {
                      handleFilesUpload(filesByFolder, fileUploadInfos);
                    }
                  });
                } else {
                  handleFilesUpload(filesByFolder, fileUploadInfos);
                }
              }
            });
        }
      }
    });
  };

  const resource = getResource();

  try {
    let files = undefined;

    const isOneDriveResource = resource.id.endsWith('_onedrive');
    const isGoogleDriveResource = resource.id.endsWith('_googledrive');

    if (typeof getFiles === 'function') {
      const result = getFiles();

      files =
        result?.map((f) => {
          if (!f.isValidFile && isOneDriveResource && isGoogleDriveResource) {
            throw new Error(f.description);
          } else {
            return f.file;
          }
        }) || [];
    } else {
      const result = await readLocalFolders();

      files =
        result?.map((f) => {
          if (!f.isValidFile && isOneDriveResource && isGoogleDriveResource) {
            throw new Error(f.description);
          } else {
            return f.file;
          }
        }) || [];
    }

    filesToUploadCount = files.length;
    const paths = files.map((a) => a.file.webkitRelativePath);
    files.forEach((a) => {
      a.filepath = a.file.webkitRelativePath;
    });
    const dirTree = getDirTreeFromFilePaths(paths);
    const rootfolderNames = _.uniqBy(dirTree.filter((f) => !f.isFile).map((d) => d.name));

    if (rootfolderNames.length) {
      api.getFilesInDestinationFolder(apiOptions, rootfolderNames, resource.id).then((existingFiles) => {
        const existingfileNames = _.uniqBy(existingFiles, 'name').map((x) => x.name);
        if (existingfileNames.length) {
          showRenameSelectedFilesModal(existingfileNames, rootContainer, () => {
            createFolderTree(files, resource, dirTree);
          });
        } else {
          createFolderTree(files, resource, dirTree);
        }
      });
    } else {
      createFolderTree(files, resource, dirTree);
    }
  } catch (err) {
    notification.error({
      message: 'Error',
      icon: '',
      top: 55,
      duration: 10,
      getContainer: rootContainer,
      description: err.message ? err.message : 'Something went wrong',
    });
  }
}

export default (apiOptions, actions) => {
  const localeLabel = getMess(apiOptions.locale, label);
  const { getResource, ownerType } = actions;
  return {
    id: label,
    icon: { icon: icons.folderUpload },
    label: localeLabel,
    shouldBeAvailable: (apiOptions) => {
      const resource = getResource();

      if (
        !resource ||
        !resource.capabilities ||
        +ownerType === fileOwnerTypes.InsuredDefaultFolderStructure ||
        +ownerType === fileOwnerTypes.PolicyDefaultFolderStructure ||
        resource.id.endsWith('_googledrive') ||
        resource.id.endsWith('_onedrive')
      ) {
        return false;
      }

      return resource.capabilities.canAddChildren;
    },
    availableInContexts: ['files-view', 'new-button'],
    handler: () => handler(apiOptions, actions),
    handlerOverride: (apiOptions, actions) => handler(apiOptions, actions),
  };
};
